/**
 * ShareLinkManager — Admin panel for creating/managing public share links
 *
 * Rendered as a tab inside PlanDetailPage. Lets CSMs generate
 * shareable URLs for an onboarding plan (or specific task).
 */

import { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Link2, Plus, Copy, Trash2, Eye, Lock, Calendar, CheckCircle, ExternalLink,
  Loader2, Shield,
} from 'lucide-react';
import {
  createShareLink, listShareLinks, revokeShareLink,
  type ShareLink,
} from '@/services/api/onboardingPortal';
import type { PlanTask } from '../../../api/onboardingApi';

interface Props {
  planId: number;
  tasks: PlanTask[];
}

export default function ShareLinkManager({ planId, tasks }: Props) {
  const [links, setLinks] = useState<ShareLink[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [copied, setCopied] = useState<string | null>(null);

  // Create form
  const [label, setLabel] = useState('');
  const [accessLevel, setAccessLevel] = useState('view');
  const [password, setPassword] = useState('');
  const [expiresDays, setExpiresDays] = useState('30');
  const [taskId, setTaskId] = useState<string>('');
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listShareLinks(planId);
      setLinks(data);
    } catch { /* ignore */ }
    setLoading(false);
  }, [planId]);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    setCreating(true);
    try {
      await createShareLink(planId, {
        label: label || undefined,
        access_level: accessLevel,
        password: password || undefined,
        expires_in_days: expiresDays ? parseInt(expiresDays) : undefined,
        task_id: taskId ? parseInt(taskId) : undefined,
      });
      setShowCreate(false);
      setLabel('');
      setPassword('');
      setTaskId('');
      setAccessLevel('view');
      setExpiresDays('30');
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to create link');
    }
    setCreating(false);
  };

  const handleRevoke = async (token: string) => {
    if (!confirm('Revoke this share link? Anyone with this URL will lose access.')) return;
    try {
      await revokeShareLink(token);
      load();
    } catch (err: any) {
      alert(err.response?.data?.detail || 'Failed to revoke');
    }
  };

  const copyUrl = (link: ShareLink) => {
    const fullUrl = `${window.location.origin}${link.url}`;
    navigator.clipboard.writeText(fullUrl);
    setCopied(link.token);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="font-medium text-sm text-gray-500 flex items-center gap-1.5">
          <Link2 className="h-4 w-4" />
          Share Links ({links.length})
        </h3>
        <Button size="sm" onClick={() => setShowCreate(true)}>
          <Plus className="h-4 w-4 mr-1" />
          Create Link
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-8">
          <Loader2 className="h-5 w-5 animate-spin text-gray-400" />
        </div>
      ) : links.length === 0 ? (
        <Card>
          <CardContent className="py-8 text-center">
            <Link2 className="h-8 w-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-500">No share links yet</p>
            <p className="text-xs text-gray-400 mt-1">Create a link to share this plan with external stakeholders</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-2">
          {links.map(link => (
            <Card key={link.token} className="overflow-hidden">
              <CardContent className="py-3">
                <div className="flex items-center justify-between">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <span className="text-sm font-medium truncate">
                        {link.label || (link.task_id ? `Task #${link.task_id}` : 'Full Plan')}
                      </span>
                      <Badge variant="outline" className="text-xs">{link.access_level}</Badge>
                      {link.has_password && (
                        <Badge variant="outline" className="text-xs">
                          <Lock className="h-3 w-3 mr-0.5" />
                          Protected
                        </Badge>
                      )}
                    </div>
                    <div className="flex items-center gap-3 text-xs text-gray-400">
                      <span className="flex items-center gap-1">
                        <Eye className="h-3 w-3" />
                        {link.view_count} views
                      </span>
                      {link.expires_at && (
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Expires {new Date(link.expires_at).toLocaleDateString()}
                        </span>
                      )}
                      <span>Created {new Date(link.created_at).toLocaleDateString()}</span>
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0 ml-2">
                    <Button variant="ghost" size="sm" onClick={() => copyUrl(link)} title="Copy link">
                      {copied === link.token ? (
                        <CheckCircle className="h-4 w-4 text-green-600" />
                      ) : (
                        <Copy className="h-4 w-4" />
                      )}
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => window.open(link.url, '_blank')}
                      title="Preview"
                    >
                      <ExternalLink className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost" size="sm"
                      onClick={() => handleRevoke(link.token)}
                      className="text-red-500 hover:text-red-700"
                      title="Revoke"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create dialog */}
      {showCreate && (
        <Dialog open onOpenChange={() => setShowCreate(false)}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Create Share Link</DialogTitle>
              <DialogDescription>Generate a public URL to share this onboarding plan</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Label (optional)</Label>
                <Input value={label} onChange={e => setLabel(e.target.value)} placeholder="e.g. For CFO review" />
              </div>

              <div>
                <Label>Scope</Label>
                <Select value={taskId} onValueChange={setTaskId}>
                  <SelectTrigger><SelectValue placeholder="Entire plan" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="">Entire plan</SelectItem>
                    {tasks.map(t => (
                      <SelectItem key={t.id} value={String(t.id)}>{t.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Access Level</Label>
                <Select value={accessLevel} onValueChange={setAccessLevel}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="view">View Only</SelectItem>
                    <SelectItem value="comment">Can Comment</SelectItem>
                    <SelectItem value="update_status">Can Update Status</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Password (optional)</Label>
                <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Leave blank for no password" />
              </div>

              <div>
                <Label>Expires In (days)</Label>
                <Input type="number" value={expiresDays} onChange={e => setExpiresDays(e.target.value)} placeholder="30" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
              <Button onClick={handleCreate} disabled={creating}>
                {creating ? 'Creating...' : 'Create Link'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
